import { ManagerAndDirectReports } from "app/containers/AdvancedCsvUpload/types";
import {
  ChangesSummary,
  DeactivateUserModalSteps,
  UpdateOrganizationalStructurePayload,
  UserResponsibilities,
} from "./types";
import { Team } from "app/containers/Global/types";
import { getS } from "utils/helperFunctions";
import { Department } from "app/containers/AdminConsole/types";

export const getDeactivateUserModalStepsArray = (
  userResponsibilities: UserResponsibilities | null
): DeactivateUserModalSteps[] => {
  const steps: DeactivateUserModalSteps[] = [];
  if (userResponsibilities?.userAccountIdsOfAutomaticTeam?.length) {
    steps.push("reassignDirectReports");
  }
  if (userResponsibilities?.teamIdsUserSoloLeads?.length) {
    steps.push("reassignTeamLeaders");
  }
  if (userResponsibilities?.departmentIdsUserLeads?.length) {
    steps.push("reassignDepartmentLeaders");
  }

  if (steps.length !== 0) {
    // add responsibilitiesSummary to the start of the array
    steps.unshift("responsibilitiesSummary");
    // add reviewChanges to the end of the array
    steps.push("reviewChanges");
  }

  return steps;
};

export const getUserResponsibilities = (
  teamsAndDepartmentsUserIsLeaderOf: {
    teams: number[];
    departments?: number[];
  } | null,
  managerAndDirectReports: ManagerAndDirectReports | null,
  teamsById: { [key: number]: Team },
  hasAutomaticTeamsEnabled: boolean | null
): UserResponsibilities => {
  const teamsUserIsSoleLeaderOf =
    teamsAndDepartmentsUserIsLeaderOf?.teams.filter((teamId) => {
      const team = teamsById[teamId];
      if (!team || team.isAutomatic) return false;
      return team.teamLeadUserAccountIds?.length === 1;
    });

  return {
    departmentIdsUserLeads: teamsAndDepartmentsUserIsLeaderOf?.departments,
    teamIdsUserSoloLeads: teamsUserIsSoleLeaderOf,
    userAccountIdsOfAutomaticTeam: hasAutomaticTeamsEnabled
      ? managerAndDirectReports?.directReportRelationships?.map(
          (relationship) => relationship.employeeUserAccountId
        )
      : undefined,
  };
};

export const getResponsibilitiesSummaryArray = (
  userResponsibilities: UserResponsibilities | null,
  teamsById: { [key: number]: Team },
  departmentsById: { [key: number]: Department }
) => {
  const summaryArray: JSX.Element[] = [];

  if (userResponsibilities?.userAccountIdsOfAutomaticTeam?.length) {
    summaryArray.push(
      <li key="direct-reports">
        <p>
          Manages{" "}
          <b>
            {userResponsibilities.userAccountIdsOfAutomaticTeam.length} direct
            report
            {getS(userResponsibilities.userAccountIdsOfAutomaticTeam.length)}
          </b>{" "}
          (Automatic Team)
        </p>
      </li>
    );
  }

  if (userResponsibilities?.teamIdsUserSoloLeads?.length) {
    const teamNames = userResponsibilities.teamIdsUserSoloLeads
      .map((teamId) => {
        const team = teamsById[teamId];
        if (!team) return "";
        return team.teamName;
      })
      .join(", ");
    summaryArray.push(
      <li key="team-leaders">
        <p>
          Leads{" "}
          <b>
            {userResponsibilities.teamIdsUserSoloLeads.length} manual team
            {getS(userResponsibilities.teamIdsUserSoloLeads.length)}
          </b>{" "}
          ({teamNames})
        </p>
      </li>
    );
  }

  if (userResponsibilities?.departmentIdsUserLeads?.length) {
    const departmentNames = userResponsibilities.departmentIdsUserLeads
      .map((departmentId) => {
        const department = departmentsById[departmentId];
        if (!department) return "";
        return department.name;
      })
      .join(", ");
    summaryArray.push(
      <li key="department-leaders">
        <p>
          Leads{" "}
          <b>
            {userResponsibilities.departmentIdsUserLeads.length} department
            {getS(userResponsibilities.departmentIdsUserLeads.length)}
          </b>{" "}
          ({departmentNames})
        </p>
      </li>
    );
  }

  return summaryArray;
};

export const getManagementScopeText = (
  userResponsibilities: UserResponsibilities | null
) => {
  const parts: string[] = [];

  if (userResponsibilities?.departmentIdsUserLeads?.length) {
    parts.push("departments");
  }

  if (userResponsibilities?.teamIdsUserSoloLeads?.length) {
    parts.push("teams");
  }

  if (userResponsibilities?.userAccountIdsOfAutomaticTeam?.length) {
    parts.push("direct reports");
  }

  if (!parts.length) return "";

  if (parts.length === 1) {
    return parts[0];
  }

  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`;
  }

  return `${parts.slice(0, -1).join(", ")}, and ${parts.at(-1)}`;
};

export const constructPayloadFromChangesSummary = (
  changesSummary: ChangesSummary,
  directReportsUserAccountIds: number[]
): UpdateOrganizationalStructurePayload => {
  const payload: UpdateOrganizationalStructurePayload = {};

  if (changesSummary.teamChanges) {
    payload.teamChanges = Object.entries(changesSummary.teamChanges).map(
      ([teamId, teamChange]) => ({
        teamId: Number(teamId),
        deleteTeam: teamChange.deleteTeam,
        newTeamLeaderUserAccountId: teamChange.newTeamLeaderUserAccountId,
      })
    );
  }

  if (changesSummary.departmentChanges) {
    payload.departmentChanges = Object.entries(
      changesSummary.departmentChanges
    ).map(([departmentId, departmentChange]) => ({
      departmentId: Number(departmentId),
      newDepartmentLeaderUserAccountId:
        departmentChange.newDepartmentLeaderUserAccountId ?? null,
    }));
  }

  if (changesSummary.automaticTeamChanges) {
    payload.managerChanges = directReportsUserAccountIds.map(
      (employeeUserAccountId) => ({
        employeeUserAccountId,
        newManagerUserAccountId: changesSummary.automaticTeamChanges
          ?.unassignManagerForAllUsers
          ? null
          : changesSummary.automaticTeamChanges?.newManagerUserAccountId,
      })
    );
  }

  return payload;
};

export const getSuccessToastMessageFromPayload = (
  payload: UpdateOrganizationalStructurePayload
) => {
  const parts: string[] = [];

  if (payload.teamChanges?.length) {
    parts.push("teams");
  }

  if (payload.departmentChanges?.length) {
    parts.push("departments");
  }

  if (payload.managerChanges?.length) {
    parts.push("direct reports");
  }

  if (!parts.length) return "";
  // for the first item we capitalize the first letter
  parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);

  if (parts.length === 1) {
    return `${parts[0]} changes were made successfully!`;
  }

  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]} changes were made successfully!`;
  }

  return `${parts.slice(0, -1).join(", ")}, and ${parts.at(
    -1
  )} changes were made successfully!`;
};
